// Cours premium Plateau n°1 — "Tu découvres • Tu ressens • Tu contrôles ta moto"
// Plateau A2 — 14 chapitres pédagogiques (sans visuels, trame identique aux cours Circulation)
// Session 1 : découvrir la moto avant le parcours

export const cours = {
  slug: 'premiere-plateau-a2',
  title: 'Ma 1ʳᵉ séance plateau A2',
  subtitle: 'Tu découvres • Tu ressens • Tu contrôles ta moto',
  duree: '2 heures — plateau école',
  cover: '/images/cours/plateau/session-1.png',
  mantra: 'REGARDE → RESSENS → DOSE → COORDONNE → RECOMMENCE',
  intro: "C'est ta première vraie séance de travail sur le plateau. Aujourd'hui, je ne te demande pas d'être rapide. Je ne te demande pas de connaître le parcours par cœur. Je veux d'abord que tu comprennes comment ta moto fonctionne et comment tes actions influencent son comportement. Avant de chercher à maîtriser le parcours, tu apprends à maîtriser ta moto.",
  sections: [
    {
      num: '01',
      kicker: 'Contact machine',
      title: 'Tu prends contact avec ta moto',
      lead: "Avant de démarrer, prends quelques minutes pour la découvrir. Installe-toi dessus, pose tes mains sur le guidon, tes pieds sur les repose-pieds. Regarde autour de toi. Je vais te présenter les commandes dont tu vas avoir besoin — ne cherche pas encore à tout mémoriser : manipule, regarde, puis essaie de les retrouver sans quitter la route des yeux.",
      type: 'list',
      list: [
        { label: 'MAIN GAUCHE', desc: 'Embrayage · clignotants · avertisseur sonore · commandes d\'éclairage selon la moto.' },
        { label: 'MAIN DROITE', desc: 'Accélérateur · frein avant · démarreur · coupe-circuit.' },
        { label: 'PIED GAUCHE', desc: 'Sélecteur de vitesses.' },
        { label: 'PIED DROIT', desc: 'Frein arrière.' },
      ],
      retenir: 'TON PREMIER OBJECTIF : COMMENCER À SAVOIR OÙ SE TROUVENT TES COMMANDES.',
    },
    {
      num: '02',
      kicker: 'Installation',
      title: 'Tu t\'installes sur ta moto',
      lead: "Ta position influence directement ta capacité à contrôler la moto. Assieds-toi naturellement — ne cherche pas une position artificielle. Si ton corps est crispé, la moto devient plus difficile à contrôler.",
      type: 'list',
      list: [
        { label: 'PIEDS', desc: 'Placés correctement sur les repose-pieds pour utiliser les commandes sans les chercher.' },
        { label: 'GENOUX', desc: 'Naturellement au contact de la moto, sans les serrer inutilement.' },
        { label: 'BASSIN', desc: 'Disponible et suffisamment détendu.' },
        { label: 'DOS', desc: 'Position naturelle, ni cambré ni voûté.' },
        { label: 'BRAS', desc: 'Non verrouillés : ils doivent pouvoir absorber les mouvements.' },
        { label: 'MAINS', desc: 'Précision pour agir, sans t\'agripper inutilement au guidon.' },
        { label: 'REGARD', desc: 'Reste haut, orienté loin devant toi.' },
      ],
      retenir: 'UNE MOTO SE PILOTE AVEC PRÉCISION, PAS AVEC DE LA FORCE.',
    },
    {
      num: '03',
      kicker: 'Sans moteur',
      title: 'Tu apprends à déplacer ta moto sans moteur',
      lead: "Avant de rouler, tu dois savoir manipuler ta moto. Elle peut sembler lourde, mais tu vas apprendre à utiliser son équilibre plutôt que ta force. Lorsque tu la déplaces à côté de toi, reste suffisamment proche pour la contrôler : plus elle s'incline, plus son poids devient difficile à retenir.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'BÉQUILLER', desc: 'Poser la moto sur sa béquille latérale avec méthode et douceur.' },
        { time: '2', label: 'DÉBÉQUILLER', desc: 'Redresser la moto en utilisant son équilibre, pas ta force.' },
        { time: '3', label: 'DÉPLACER', desc: 'Marcher à côté d\'elle, en la maintenant proche de ton corps.' },
        { time: '4', label: 'AVANCER', desc: 'Pousser avec les jambes, guidon droit.' },
        { time: '5', label: 'RECULER', desc: 'Doser l\'effort, regard porté loin, sans à-coup.' },
        { time: '6', label: 'POSITIONNER', desc: 'Placer la moto précisément à l\'endroit voulu.' },
      ],
      retenir: 'CHERCHE L\'ÉQUILIBRE AVANT DE CHERCHER LA FORCE.',
    },
    {
      num: '04',
      kicker: 'Démarrer / arrêter',
      title: 'Tu démarres et tu t\'arrêtes',
      lead: "Nous allons mettre la moto en mouvement. Pas besoin de vitesse. Je veux simplement que tu comprennes comment elle commence à avancer. Tu démarres, tu vérifies ton environnement, tu engages la première lorsque les conditions sont réunies, puis tu relâches progressivement l'embrayage.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'DÉMARRER', desc: 'Contact, coupe-circuit, point mort vérifié, moteur en marche.' },
        { time: '2', label: 'ENGAGER', desc: 'Première engagée uniquement quand l\'environnement est sûr.' },
        { time: '3', label: 'AVANCER', desc: 'Relâcher progressivement l\'embrayage : la moto veut avancer.' },
        { time: '4', label: 'ARRÊTER', desc: 'Progressivement, embrayage débrayé, pied à terre.' },
        { time: '5', label: 'RECOMMENCER', desc: 'Répéter jusqu\'à supprimer l\'hésitation entre main, pieds et moto.' },
      ],
      retenir: 'JE VEUX SUPPRIMER L\'HÉSITATION ENTRE TA MAIN, TES PIEDS ET LA MOTO.',
    },
    {
      num: '05',
      kicker: 'Point de patinage',
      title: 'Tu découvres le point de patinage',
      lead: "C'est l'une des notions fondamentales de ta première séance. Serre l'embrayage, engage la première, puis relâche très progressivement le levier. Tu vas sentir le moment où la transmission commence à entraîner la moto : voilà ton point de patinage. Ne cherche pas simplement un endroit précis sur la course du levier — cherche surtout à ressentir la réaction de la moto.",
      type: 'cards3',
      cards: [
        { title: 'ELLE TIRE LÉGÈREMENT', desc: 'La transmission commence à entraîner la roue.' },
        { title: 'LE RÉGIME ÉVOLUE', desc: 'Un petit changement dans le son du moteur.' },
        { title: 'ELLE VEUT AVANCER', desc: 'Tu peux la laisser rouler sans accélérer.' },
      ],
      retenir: 'NE REGARDE PAS TON LEVIER. RESSENS TA MOTO.',
    },
    {
      num: '06',
      kicker: 'Doucement sur les gaz',
      title: 'Tu coordonnes accélérateur et embrayage — doucement sur les gaz',
      lead: "Voici la première grande notion à comprendre : on ne pilote pas une moto « avec le poignet ». On la pilote « doucement sur les gaz », c'est-à-dire avec une action fine et progressive sur l'accélérateur. L'idée n'est pas de faire monter le moteur dans les tours, mais de trouver une motricité continue et régulière. Tu maintiens l'accélérateur stable, et tu doses le déplacement avec ton embrayage.",
      type: 'cards3',
      cards: [
        { title: 'STABLE', desc: 'L\'accélérateur reste dans une plage constante — pas d\'aller-retour.' },
        { title: 'PROGRESSIF', desc: 'Chaque geste s\'installe dans le temps, jamais d\'un coup.' },
        { title: 'FLUIDE', desc: 'La moto avance sans à-coups, la roue tire régulièrement.' },
      ],
      body: "Concrètement : si tu coupes puis remets constamment les gaz, la roue s'entraîne par petits coups → la moto perd sa régularité et ton équilibre en pâtit. À l'inverse, un accélérateur maintenu doucement et stable + un embrayage dosé = une moto qui « glisse » toute seule à basse allure. C'est ce qu'on appelle piloter « doucement sur les gaz » : la douceur du poignet devient la source de ta stabilité.",
      retenir: 'DOUCEMENT SUR LES GAZ = ACCÉLÉRATEUR STABLE + EMBRAYAGE DOSÉ = MOTO RÉGULIÈRE.',
    },
    {
      num: '07',
      kicker: 'Frein arrière',
      title: 'Tu découvres le rôle du frein arrière à basse allure',
      lead: "Nous ajoutons une troisième commande : ton frein arrière. À basse allure, il peut t'aider à contrôler et stabiliser ton déplacement lorsqu'il est utilisé avec finesse. Tu conserves ta motricité, puis tu appliques progressivement une légère action sur le frein arrière. Observe ce qui se passe : la moto ralentit, mais tu conserves une continuité dans son déplacement.",
      type: 'cards3',
      cards: [
        { title: 'ACCÉLÉRATEUR', desc: 'Il fournit l\'énergie de manière stable.' },
        { title: 'EMBRAYAGE', desc: 'Il dose la transmission de cette énergie.' },
        { title: 'FREIN ARRIÈRE', desc: 'Il stabilise et régule le déplacement à basse allure.' },
      ],
      body: "Coordonner ces trois commandes en même temps peut sembler compliqué au début — c'est normal : tu demandes à plusieurs parties de ton corps de réaliser simultanément des actions différentes. Nous allons répéter jusqu'à ce que cette coordination devienne plus naturelle.",
      retenir: 'ACCÉLÉRATEUR + EMBRAYAGE + FREIN ARRIÈRE : LA TRIADE DE LA BASSE ALLURE.',
    },
    {
      num: '08',
      kicker: 'Regard directeur',
      title: 'Ton regard commence à guider ta moto',
      lead: "Lorsque tu débutes, ton regard est souvent attiré par un cône, la roue avant, le guidon, le sol, ou l'endroit où tu as peur de passer. Mais ta moto suit fortement la direction que tu construis avec ton regard et tes actions. Nous allons donc apprendre à lever les yeux.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'LOIN DEVANT TOI', desc: 'Ton regard part chercher l\'information à distance.' },
        { time: '2', label: 'VERS LA PROCHAINE ZONE', desc: 'Tu prépares déjà la portion qui arrive.' },
        { time: '3', label: 'VERS LA SORTIE', desc: 'Si tu tournes, ton regard prépare le changement de direction.' },
      ],
      retenir: 'NE REGARDE PAS CE QUE TU VEUX ÉVITER. CHERCHE L\'ENDROIT OÙ TU VEUX ALLER.',
    },
    {
      num: '09',
      kicker: 'Équilibre en phase',
      title: 'Tu apprends à rouler lentement — l\'équilibre en phase',
      lead: "Deuxième grande notion : rouler lentement sur une moto ne demande PAS un seul geste, mais 6 gestes coordonnés au même moment. Quand tes 6 actions se déclenchent ensemble et se renforcent, on dit qu'elles sont « en phase » — comme les musiciens d'un orchestre qui jouent la même note au bon moment. À l'inverse, si une seule commande sort du rythme (regard qui tombe, main qui coupe, corps qui se crispe), tout l'ensemble se déstabilise. C'est ça, l'équilibre en phase.",
      type: 'list',
      list: [
        { label: 'REGARD', desc: 'Porté loin devant — jamais sur la roue ou le guidon. C\'est lui qui « tire » la moto.' },
        { label: 'MOTRICITÉ', desc: 'Continue, jamais coupée brutalement — la roue reste entraînée.' },
        { label: 'EMBRAYAGE', desc: 'Dosé finement dans la zone du point de patinage.' },
        { label: 'ACCÉLÉRATEUR', desc: 'Doucement sur les gaz : stable, sans à-coups (cf. section 06).' },
        { label: 'FREIN ARRIÈRE', desc: 'Légèrement appliqué pour stabiliser sans casser la continuité.' },
        { label: 'CORPS', desc: 'Détendu, bras souples, tête haute : l\'inclinaison naturelle vit avec la moto.' },
      ],
      body: "Comment savoir si tu es « en phase » ? La moto avance toute seule, régulière, sans que tu penses à chaque commande — elles agissent ensemble. À l'inverse, si tu sens des à-coups, si tu poses le pied sans le vouloir, si tu regardes le sol : une de tes 6 commandes est sortie du rythme. Analyse tranquillement : Où était mon regard ? Ma motricité a-t-elle coupé ? Mes bras étaient-ils détendus ? Chaque erreur devient une info pour ton prochain passage. On ne cherche pas à rouler lentement d'abord — on cherche à être stable d'abord. La lenteur viendra ensuite naturellement.",
      retenir: 'L\'ÉQUILIBRE EN PHASE = 6 COMMANDES QUI JOUENT ENSEMBLE. D\'ABORD STABLE, ENSUITE LENT.',
    },
    {
      num: '10',
      kicker: 'Changer de direction',
      title: 'Tu apprends à changer de direction à faible allure',
      lead: "Nous allons commencer à tourner davantage. Avant le changement de direction, ton regard prépare la sortie — pas uniquement devant ta roue. La moto va devoir changer de direction et s'incliner suffisamment pour suivre la trajectoire. Si tu te crispes sur le guidon, tu risques de rendre la moto plus difficile à diriger.",
      type: 'cards3',
      cards: [
        { title: 'REGARDE', desc: 'Là où tu veux aller, pas là où tu es.' },
        { title: 'ORIENTE', desc: 'Le regard construit la trajectoire, le guidon suit.' },
        { title: 'ACCOMPAGNE & RESSORS', desc: 'Motricité conservée, sortie préparée par le regard.' },
      ],
      retenir: 'REGARDE → ORIENTE → ACCOMPAGNE → RESSORS.',
    },
    {
      num: '11',
      kicker: 'Premier demi-tour',
      title: 'Tu construis ton premier demi-tour',
      lead: "Le demi-tour concentre plusieurs difficultés. Avant d'entrer, cherche déjà la sortie avec ton regard. Pendant le demi-tour, évite de fixer l'intérieur ou le sol : si tu regardes trop près, ton rayon risque de s'ouvrir ou ta conduite de devenir hésitante. Au début, je peux te donner beaucoup de repères, puis progressivement je les enlève.",
      type: 'list',
      list: [
        { label: 'RALENTIR', desc: 'Arriver à une allure compatible avec le rayon souhaité.' },
        { label: 'PRÉPARER LE REGARD', desc: 'Chercher la sortie avant l\'entrée.' },
        { label: 'CONSERVER LA MOTRICITÉ', desc: 'Motricité continue, embrayage dosé.' },
        { label: 'ORIENTER LA MOTO', desc: 'Le guidon suit la direction du regard.' },
        { label: 'ACCEPTER L\'INCLINAISON', desc: 'La moto doit s\'incliner suffisamment pour tourner.' },
        { label: 'RECHERCHER LA SORTIE', desc: 'Le regard tire la moto hors de la manœuvre.' },
      ],
      retenir: 'LE BUT N\'EST PAS DE RÉUSSIR UNE FOIS. LE BUT EST DE COMPRENDRE POURQUOI TU RÉUSSIS.',
    },
    {
      num: '12',
      kicker: 'Changement de rapports',
      title: 'Tu découvres les changements de rapports',
      lead: "Lorsque ton niveau et les conditions de l'exercice le permettent, nous augmentons progressivement l'allure. Tu vas commencer à travailler plus précisément : 1re → 2e → puis rétrogradage lorsque nécessaire. L'objectif n'est pas seulement de déplacer ton sélecteur : tu dois conserver ta trajectoire, ton regard, ton équilibre et une allure adaptée.",
      type: 'cards3',
      cards: [
        { title: 'TRAJECTOIRE', desc: 'Elle ne se dégrade pas pendant le passage de rapport.' },
        { title: 'REGARD', desc: 'Toujours haut, jamais sur le sélecteur.' },
        { title: 'ÉQUILIBRE', desc: 'Le passage de rapport n\'introduit pas d\'à-coup.' },
      ],
      retenir: 'TU CHANGES DE RAPPORT SANS CESSER DE CONDUIRE LA MOTO.',
    },
    {
      num: '13',
      kicker: 'Premier freinage',
      title: 'Tu découvres le freinage',
      lead: "Nous allons travailler une autre compétence essentielle. Avant de rechercher un freinage plus important, nous allons d'abord rechercher le contrôle. Tu regardes devant toi, tu stabilises ta trajectoire, puis tu agis progressivement sur tes commandes de freinage.",
      type: 'list',
      list: [
        { label: 'LA DÉCÉLÉRATION', desc: 'La moto ralentit de manière contrôlée, sans blocage.' },
        { label: 'LE TRANSFERT DE CHARGE', desc: 'Le poids se déplace vers l\'avant : tu le sens dans les bras.' },
        { label: 'L\'ÉQUILIBRE', desc: 'La moto reste stable, sans piqué violent ni écart.' },
        { label: 'L\'ACTION DES FREINS', desc: 'Progressive, dosée, coordonnée avant/arrière.' },
      ],
      body: "Au début, nous travaillons à une allure adaptée à ton niveau. Nous augmenterons progressivement les exigences lorsque ta technique sera suffisamment maîtrisée.",
      retenir: 'UN BON FREINAGE NE COMMENCE PAS PAR LA FORCE. IL COMMENCE PAR UNE ACTION MAÎTRISÉE ET PROGRESSIVE.',
    },
    {
      num: '14',
      kicker: 'Bilan Plateau N°1',
      title: 'Ton bilan plateau n°1',
      lead: "Nous terminons ta première séance. Avant que je te donne mon avis, je veux d'abord connaître le tien : comment t'es-tu senti ? Puis nous reprenons les compétences travaillées, avec trois niveaux d'appréciation.",
      type: 'bilan',
      questions: [
        'Identifier tes principales commandes ?',
        'T\'installer correctement ?',
        'Déplacer ta moto sans moteur ?',
        'Démarrer et t\'arrêter ?',
        'Ressentir ton point de patinage ?',
        'Coordonner accélérateur et embrayage ?',
        'Utiliser progressivement le frein arrière à basse allure ?',
        'Lever ton regard ?',
        'Conserver une allure lente ?',
        'Changer de direction ?',
        'Réaliser un premier demi-tour ?',
        'Changer de rapport ?',
        'Réaliser un premier freinage maîtrisé ?',
      ],
      blocks: [
        { title: '🟢 JE COMMENCE À MAÎTRISER', color: 'green' },
        { title: '🟠 JE SUIS EN COURS D\'ACQUISITION', color: 'amber' },
        { title: '🔴 J\'AI ENCORE BESOIN D\'AIDE', color: 'red' },
      ],
      body: "Puis nous déterminons ensemble : ce que tu as réussi · ce qui t'a posé problème · ton objectif pour la Session N°2. Aujourd'hui, tu n'as pas simplement appris à déplacer une moto entre des cônes. Tu as commencé à comprendre pourquoi elle avance, pourquoi elle ralentit, comment elle s'équilibre et comment tes actions influencent son comportement.",
      retenir: 'TU NE CHERCHES PAS ENCORE À DOMINER TA MOTO. TU APPRENDS À TRAVAILLER AVEC ELLE.',
    },
  ],
};
